import React, { useEffect, useState } from "react";
import API_URL from "../apiConfig";
import "../styles/Income.css";

/*
  IncomeEntryModal.jsx

  - Final, cleaned version. No example JSX in comments.
  - Matches your backend routes and payload expectations:
      POST /api/income_expense/income
      PUT  /api/income_expense/income/:id
    Payload keys: { receipt_no, date, account_head, particulars, reference, amount, mode,
                    student_name, roll_no, batch_id, course_year, session_year, source_fee_id }
  - Ensures optional fields are sent as null (not undefined) to avoid DB bind errors.
  - Sanitizes amount, validates required fields and surfaces server errors.
  - Calls onSave(result) on success so parent can refresh its list.
*/

export default function IncomeEntryModal({
  token,
  income = null,
  editMode = false,
  onClose = () => {},
  onSave = () => {},
  heads = [],
  onAddHead = () => {}
}) {
  const todayYMD = () => new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    receipt_no: "",
    date: todayYMD(),
    account_head: (heads && heads.length) ? heads[0] : "",
    particulars: "",
    reference: "",
    amount: "",
    mode: "cash",
    student_name: "",
    roll_no: "",
    batch_id: "",
    course_year: "",
    session_year: "",
    source_fee_id: ""
  });

  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!income) {
      setForm(f => ({ ...f, account_head: (heads && heads.length) ? heads[0] : f.account_head }));
      return;
    }

    const receiptNo = income.receipt_no || income.fee_id || "";
    const dateVal = (income.date || income.payment_date || income.created_at || "").slice(0, 10) || todayYMD();
    const accountHead = income.account_head || income.head || ((heads && heads.length) ? heads[0] : "");
    const particulars = income.particulars || "";
    const reference = income.reference || "";
    const rawAmount = (income.amount !== undefined && income.amount !== null)
      ? income.amount
      : (income.amount_paid !== undefined ? income.amount_paid : "");
    let amountVal = "";
    if (rawAmount !== "" && rawAmount !== null && rawAmount !== undefined) {
      const parsed = parseFloat(String(rawAmount).replace(/,/g, "").trim());
      if (!isNaN(parsed)) amountVal = parsed.toFixed(2);
    }
    const mode = income.mode || "cash";
    const student_name = income.student_name || income.name || "";
    const roll_no = income.roll_no || "";
    const batch_id = (income.batch_id !== undefined && income.batch_id !== null) ? income.batch_id : (income.batch !== undefined && income.batch !== null ? income.batch : "");
    const course_year = income.course_year || "";
    const session_year = income.session_year || "";
    const source_fee_id = income.source_fee_id || income.sourceFeeId || "";

    setForm({
      receipt_no: receiptNo,
      date: dateVal,
      account_head: accountHead,
      particulars,
      reference,
      amount: amountVal,
      mode,
      student_name,
      roll_no,
      batch_id,
      course_year,
      session_year,
      source_fee_id
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [income, heads]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function handleAddHead() {
    const v = window.prompt("Enter new account head (e.g. Donation):");
    if (!v || !v.trim()) return;
    const head = v.trim();
    try { onAddHead(head); } catch (err) { /* ignore parent errors */ }
    setForm(prev => ({ ...prev, account_head: head }));
  }

  function sanitizeAmount(value) {
    if (value === null || value === undefined) return NaN;
    // remove commas and whitespace then keep only digits, dot and minus using character filtering (avoids regex hyphen warnings)
    const noCommas = String(value).replace(/,/g, "").trim();
    const allowedChars = "0123456789.-";
    const cleanedChars = Array.from(noCommas).filter(ch => allowedChars.indexOf(ch) !== -1);
    const cleaned = cleanedChars.join("");
    if (cleaned === "" || cleaned === "." || cleaned === "-") return NaN;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  }

  function validate() {
    setErrorMsg("");
    if (!form.date) return "Please enter Date.";
    if (!form.account_head) return "Please select Account Head.";
    if (!form.mode) return "Please select Mode.";
    const amt = sanitizeAmount(form.amount);
    if (!Number.isFinite(amt) || amt <= 0) return `Invalid amount "${form.amount}". Enter numeric value > 0.`;
    return null;
  }

  async function handleSubmit(e) {
    e && e.preventDefault();
    const v = validate();
    if (v) { setErrorMsg(v); return; }

    setSubmitting(true);
    setErrorMsg("");

    // Build payload: ensure optional empty fields become null (so DB bind params don't contain undefined)
    const payload = {
      receipt_no: (form.receipt_no && String(form.receipt_no).trim()) ? String(form.receipt_no).trim() : null,
      date: form.date,
      account_head: form.account_head,
      particulars: (form.particulars && String(form.particulars).trim()) ? String(form.particulars).trim() : null,
      reference: (form.reference && String(form.reference).trim()) ? String(form.reference).trim() : null,
      amount: sanitizeAmount(form.amount),
      mode: form.mode,
      student_name: (form.student_name && String(form.student_name).trim()) ? String(form.student_name).trim() : null,
      roll_no: (form.roll_no && String(form.roll_no).trim()) ? String(form.roll_no).trim() : null,
      batch_id: (form.batch_id && String(form.batch_id).trim()) ? String(form.batch_id).trim() : null,
      course_year: (form.course_year && String(form.course_year).trim()) ? String(form.course_year).trim() : null,
      session_year: (form.session_year && String(form.session_year).trim()) ? String(form.session_year).trim() : null,
      source_fee_id: (form.source_fee_id && String(form.source_fee_id).trim()) ? String(form.source_fee_id).trim() : null
    };

    console.debug("[IncomeEntryModal] payload:", payload);

    try {
      let url = `${API_URL}/api/income_expense/income`;
      let method = "POST";
      if (editMode && income) {
        const id = (income.id !== undefined && income.id !== null) ? income.id : (income.fee_id !== undefined && income.fee_id !== null ? income.fee_id : null);
        if (id) {
          url = `${API_URL}/api/income_expense/income/${encodeURIComponent(id)}`;
          method = "PUT";
        }
      }

      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetch(url, { method, headers, body: JSON.stringify(payload) });
      const text = await res.text().catch(() => "");

      if (!res.ok) {
        try {
          const j = JSON.parse(text || "{}");
          const message = j.error || j.message || (j.details ? JSON.stringify(j.details) : null) || `Server returned ${res.status}`;
          setErrorMsg(String(message));
        } catch (err) {
          setErrorMsg(text || `Server returned ${res.status}`);
        }
        setSubmitting(false);
        return;
      }

      let result = null;
      try { result = JSON.parse(text || "{}"); } catch (_) { result = null; }

      try { onSave(result || {}); } catch (_) { /* ignore parent handler errors */ }
    } catch (err) {
      console.error("[IncomeEntryModal] submit error:", err);
      setErrorMsg("Network/server error while saving. See console/network tab.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" style={{ zIndex: 9999 }}>
      <div className="modal-dialog" role="dialog" aria-modal="true" style={{ maxWidth: 980 }}>
        <div className="modal-header"><h3 style={{ margin: 0 }}>{editMode ? "Edit Income" : "Add Income"}</h3></div>

        <form onSubmit={handleSubmit} className="modal-body" style={{ padding: 18 }}>
          {errorMsg && <div style={{ color: "red", marginBottom: 12 }}>{errorMsg}</div>}

          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Receipt No</label>
              <input name="receipt_no" value={form.receipt_no} onChange={handleChange} placeholder="auto or enter" style={{ width: "100%", padding: 8 }} />
            </div>

            <div style={{ width: 170 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Date</label>
              <input name="date" type="date" value={form.date} onChange={handleChange} style={{ width: "100%", padding: 8 }} />
            </div>

            <div style={{ width: 170 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Mode</label>
              <select name="mode" value={form.mode} onChange={handleChange} style={{ width: "100%", padding: 8 }}>
                <option value="cash">Cash</option>
                <option value="bank">Bank</option>
                <option value="online">Online</option>
                <option value="UPI">UPI</option>
                <option value="scholarship">Scholarship</option>
                <option value="Carryforward">Carryforward</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Account Head</label>
              <div style={{ display: "flex", gap: 8 }}>
                <select name="account_head" value={form.account_head} onChange={handleChange} style={{ flex: 1, padding: 8 }}>
                  {(heads || []).map(h => <option key={h} value={h}>{h}</option>)}
                </select>
                <button type="button" onClick={handleAddHead} style={{ padding: "8px 10px" }}>+</button>
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Particulars</label>
              <input name="particulars" value={form.particulars} onChange={handleChange} style={{ width: "100%", padding: 8 }} />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Reference</label>
              <input name="reference" value={form.reference} onChange={handleChange} style={{ width: "100%", padding: 8 }} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Amount</label>
              <input name="amount" value={form.amount} onChange={handleChange} placeholder="0.00" style={{ width: "100%", padding: 8 }} />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Student Name (optional)</label>
              <input name="student_name" value={form.student_name} onChange={handleChange} style={{ width: "100%", padding: 8 }} />
            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 6 }}>Roll No (optional)</label>
              <input name="roll_no" value={form.roll_no} onChange={handleChange} style={{ width: "100%", padding: 8 }} />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <button type="button" onClick={onClose} disabled={submitting} style={{ padding: "8px 12px" }}>Cancel</button>
            <button type="submit" disabled={submitting} style={{ padding: "8px 14px" }}>{submitting ? (editMode ? "Saving..." : "Adding...") : (editMode ? "Save" : "Add Income")}</button>
          </div>
        </form>
      </div>
    </div>
  );
}